import { useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { Switch } from "@/components/design/Switch";
import {
  ListingMailNotificationDocument,
  SetListingMailNotificationDocument,
  type ListingMailNotification,
} from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

// Die eigenen E-Mail-Benachrichtigungen zu dieser Ausschreibung: zwei Schalter, beide anfangs aus, nur die eigene
// Auswahl (@external-adr GENESIS-0085-listing-readers-get-notification-mails-only-when-they-opt-in). Steht im Reiter
// Bewerbungen, den nur sieht, wer die Bewerbungen lesen darf; ob eine Mail wirklich geht, prüft genesis bei jeder Mail
// erneut.

type Choice = Pick<ListingMailNotification, "applicationReceived" | "matchResultAvailable">;

export function ListingMailNotificationSwitches({ listingId }: { listingId: string }) {
  const intl = useIntl();
  const query = useQuery(ListingMailNotificationDocument, { variables: { id: listingId } });
  const [save, saving] = useMutation(SetListingMailNotificationDocument);
  const [failure, setFailure] = useState<unknown>(null);
  const current = query.data?.listing?.myMailNotification;
  if (!current) return null;

  const change = async (next: Choice) => {
    setFailure(null);
    try {
      await save({
        variables: { input: { listingId, ...next } },
        update(cache, { data }) {
          const saved = data?.setListingMailNotification;
          if (!saved) return;
          cache.writeQuery({
            query: ListingMailNotificationDocument,
            variables: { id: listingId },
            data: { __typename: "Query", listing: { __typename: "Listing", id: listingId, myMailNotification: saved } },
          });
        },
      });
    } catch (error) {
      setFailure(error);
    }
  };

  const applicationsLabel = intl.formatMessage({ id: "share.listings.mailNotification.applications", defaultMessage: "Neue Bewerbungen" });
  const resultsLabel = intl.formatMessage({ id: "share.listings.mailNotification.results", defaultMessage: "Match-Ergebnisse" });
  return (
    <div id="listing-mail-notifications" className="mb-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="flex items-center gap-1.5 smycles-field-value">
          {intl.formatMessage({ id: "share.listings.mailNotification.title", defaultMessage: "E-Mail an mich" })}
          <InfoTooltip ariaLabel={intl.formatMessage({ id: "share.listings.mailNotification.infoLabel", defaultMessage: "Was diese E-Mails enthalten" })}>
            {intl.formatMessage({
              id: "share.listings.mailNotification.info",
              defaultMessage: "Nur für dich und nur für diese Ausschreibung. Match-Ergebnisse: eine E-Mail je neuer Match-Version, sobald deren erstes Ergebnis vorliegt und nur wenn du das Match sehen darfst. Spätere Ergänzungen derselben Version lösen keine weitere E-Mail aus. Jede E-Mail nennt die Ausschreibung und enthält einen Link, keine Bewerberdaten. Sie kommt nur, solange du die Bewerbungen lesen darfst.",
            })}
          </InfoTooltip>
        </span>
        <span className="flex items-center gap-2">
          <Switch id="listing-mail-notifications-applications" checked={current.applicationReceived}
            disabled={saving.loading} ariaLabel={applicationsLabel}
            onChange={(applicationReceived) => void change({ applicationReceived, matchResultAvailable: current.matchResultAvailable })} />
          <span className="smycles-field-value">{applicationsLabel}</span>
        </span>
        <span className="flex items-center gap-2">
          <Switch id="listing-mail-notifications-results" checked={current.matchResultAvailable}
            disabled={saving.loading} ariaLabel={resultsLabel}
            onChange={(matchResultAvailable) => void change({ applicationReceived: current.applicationReceived, matchResultAvailable })} />
          <span className="smycles-field-value">{resultsLabel}</span>
        </span>
      </div>
      {failure ? <p role="alert" className="mt-2 text-smycles-meta text-ink-accent">{userErrorMessage(intl, failure)}</p> : null}
    </div>
  );
}
